import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import apiService from '../services/api.service'
import LoadingOverlay from '../components/loading/LoadingOverlay'
import { Loader } from '../components/loading'
import './administrador-codigos.css'

// Utilidad: color por clasificación (accesible para todos los componentes del archivo)
const getClasificacionColor = (clasificacion: string): string => {
  switch (clasificacion) {
    case 'ingreso': return '#6BAD88'
    case 'costo': return '#ECA362'
    case 'gasto': return '#60748E'
    default: return '#8B5CF6'
  }
}

// Interfaces para los tipos de datos
interface FinancialGroup {
  id?: string;
  nombre: string;
  orden: number;
  activo: boolean;
  created_at: string;
}

interface FinancialSubcategory {
  id?: string;
  id_padre?: string;
  nombre: string;
  grupo: string;
  tipo_clasificacion?: 'costo' | 'gasto' | 'ingreso';
  codigos: { [key: string]: string };
  activo: boolean;
  created_at?: string;
}

interface FincaException {
  codigo: string;
  finca: string;
  subcategoria: string;
  grupo: string;
  clasificacion: 'costo' | 'gasto' | 'ingreso';
  created_at?: string;
}

interface ApiResponse<T> {
  data: T;
  message?: string;
}

interface GroupsResponse {
  groups: FinancialGroup[];
  total: number;
}

interface SubcategoriesResponse {
  subcategories: FinancialSubcategory[];
  total: number;
  grupo_filtro?: string;
}

interface ExceptionsResponse {
  exceptions: FincaException[];
  total: number;
  filters_applied: {
    finca?: string;
    grupo?: string;
    subcategoria?: string;
    codigo?: string;
  };
}

// Normaliza subcategorías de la API: asegura que tengan `id` (p. ej. desde _id del backend)
const normalizeSubcategories = (list: FinancialSubcategory[]): FinancialSubcategory[] =>
  (list || []).map((s: FinancialSubcategory & { _id?: string }) => ({
    ...s,
    id: s.id ?? s._id
  }));

const AdministradorCodigos: React.FC = () => {
  // Estados principales
  const [activeTab, setActiveTab] = useState<'groups' | 'subcategories' | 'exceptions'>('groups');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingCodigos, setLoadingCodigos] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Estados para Grupos Financieros
  const [groups, setGroups] = useState<FinancialGroup[]>([]);
  const [showGroupModal, setShowGroupModal] = useState<boolean>(false);
  const [editingGroup, setEditingGroup] = useState<FinancialGroup | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Estados para Subcategorías Financieras
  const [subcategories, setSubcategories] = useState<FinancialSubcategory[]>([]);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState<boolean>(false);
  const [editingSubcategory, setEditingSubcategory] = useState<FinancialSubcategory | null>(null);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState<string>('');
  const [showCodigosModal, setShowCodigosModal] = useState<boolean>(false);
  const [selectedEntityForCodigos, setSelectedEntityForCodigos] = useState<{
    type: 'group' | 'subcategory' | 'finca';
    name: string;
    codigos: { [key: string]: string };
    parentName?: string;
    id_padre?: string;
  } | null>(null);
  const [showEditCodigosModal, setShowEditCodigosModal] = useState<boolean>(false);
  const [editingCodigos, setEditingCodigos] = useState<{ [key: string]: string }>({});
  const [codigoDescripciones, setCodigoDescripciones] = useState<{ [key: string]: string }>({});

  // Estados para Excepciones por Finca
  const [exceptions, setExceptions] = useState<FincaException[]>([]);
  const [showExceptionModal, setShowExceptionModal] = useState<boolean>(false);
  const [editingException, setEditingException] = useState<FincaException | null>(null);
  const [fincas, setFincas] = useState<Array<{ nombre: string }>>([]);
  const [exceptionFilters, setExceptionFilters] = useState({
    finca: '',
    grupo: '',
    subcategoria: '',
    codigo: ''
  });

  // Estados para el modal de agregar código avanzado
  const [showAddCodigoModal, setShowAddCodigoModal] = useState<boolean>(false);
  const [addCodigoForm, setAddCodigoForm] = useState({
    codigo: '',
    descripcion: '',
    tipo: 'costo' as 'costo' | 'gasto' | 'ingreso'
  });
  const [codigoExistente, setCodigoExistente] = useState<{
    existe: boolean;
    descripcion?: string;
    subcategoria?: string;
    grupo?: string;
    tipo?: string;
  }>({ existe: false });
  const [targetSubcategory, setTargetSubcategory] = useState<{ nombre: string; grupo: string; id_padre?: string } | null>(null);
  const [searchingCodigo, setSearchingCodigo] = useState<boolean>(false);

  // Estados para formularios
  const [groupForm, setGroupForm] = useState({
    nombre: '',
    orden: 0,
    activo: true
  });

  const [subcategoryForm, setSubcategoryForm] = useState({
    nombre: '',
    grupo: '',
    codigos: {} as { [key: string]: string },
    activo: true
  });

  const [exceptionForm, setExceptionForm] = useState({
    codigo: '',
    finca: '',
    subcategoria: '',
    grupo: '',
    clasificacion: 'costo' as 'costo' | 'gasto' | 'ingreso'
  });

  // Cargar datos iniciales
  useEffect(() => {
    loadGroups();
    loadSubcategories(); // Cargar subcategorías desde el inicio
    loadFincas(); // Cargar fincas disponibles
  }, []);

  useEffect(() => {
    if (activeTab === 'groups' || activeTab === 'subcategories') {
      loadSubcategories();
    }
    if (activeTab === 'exceptions') {
      loadExceptions();
    }
  }, [activeTab]);

  // Recargar subcategorías cuando ya estén cargados los grupos (p. ej. al entrar sin filtro)
  useEffect(() => {
    if ((activeTab === 'groups' || activeTab === 'subcategories') && groups.length > 0) {
      loadSubcategories();
    }
  }, [groups.length]);

  // Cargar excepciones al cambiar filtros con debounce para mejor UX
  useEffect(() => {
    if (activeTab !== 'exceptions') return;
    const timer = setTimeout(() => {
      loadExceptions();
    }, 350);
    return () => clearTimeout(timer);
  }, [exceptionFilters, activeTab]);

  // Funciones para cargar datos
  const loadGroups = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      // Intentar cargar desde la API primero
      try {
        const response = await apiService.get<GroupsResponse>('/api/financial-groups');
        console.log('📊 Grupos desde API:', response.data);
        setGroups(response.data.groups.sort((a, b) => a.orden - b.orden));
      } catch (apiError) {
        console.warn('⚠️ API no disponible, usando datos de ejemplo');
        // Datos de ejemplo si la API no está disponible
        const exampleGroups: FinancialGroup[] = [
          { nombre: 'Ventas', orden: 1, activo: true, created_at: '2025-01-01T00:00:00Z' },
          { nombre: 'Insumos', orden: 2, activo: true, created_at: '2025-01-01T00:00:00Z' },
          { nombre: 'Mano de Obra', orden: 3, activo: true, created_at: '2025-01-01T00:00:00Z' },
          { nombre: 'Gastos Operativos', orden: 4, activo: true, created_at: '2025-01-01T00:00:00Z' }
        ];
        setGroups(exampleGroups);
      }
    } catch (err: any) {
      console.error('❌ Error general al cargar grupos:', err);
      setError(err.response?.data?.message || 'Error al cargar grupos financieros');
    } finally {
      setLoading(false);
    }
  };

  const loadSubcategories = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      // Intentar cargar desde la API primero (el backend exige grupo o id_padre)
      try {
        const group = selectedGroupFilter ? groups.find(g => g.nombre === selectedGroupFilter) : null;
        const idPadre = group?.id ?? (group as any)?.id_padre;
        
        if (idPadre) {
          const response = await apiService.get<SubcategoriesResponse>(`/api/financial-subcategories?id_padre=${encodeURIComponent(idPadre)}`);
          console.log('📊 Subcategorías desde API:', response.data);
          setSubcategories(normalizeSubcategories(response.data.subcategories || []));
        } else {
          // Sin filtro de grupo: obtener subcategorías de cada grupo con id y unir
          const groupsWithId = groups.filter(g => g.id ?? (g as any)?.id_padre);
          if (groupsWithId.length === 0) {
            setSubcategories([]);
          } else {
            const results = await Promise.all(
              groupsWithId.map(g => {
                const pid = g.id ?? (g as any)?.id_padre;
                return apiService.get<SubcategoriesResponse>(`/api/financial-subcategories?id_padre=${encodeURIComponent(pid)}`);
              })
            );
            const merged = results.flatMap(r => r.data.subcategories || []);
            setSubcategories(normalizeSubcategories(merged));
          }
        }
      } catch (apiError) {
        console.warn('⚠️ API no disponible, usando datos de ejemplo');
        // Datos de ejemplo con códigos reales
        const exampleSubcategories: FinancialSubcategory[] = [
          {
            nombre: 'Ventas de Café',
            grupo: 'Ventas',
            codigos: {
              '41010101': 'ingreso',
              '41010102': 'ingreso',
              '41010103': 'ingreso',
              '41010104': 'ingreso',
              '41010105': 'ingreso',
              '41010106': 'ingreso',
              '41010107': 'ingreso',
              '41010108': 'ingreso',
              '41010109': 'ingreso',
              '41010110': 'ingreso',
              '41010111': 'ingreso',
              '41010112': 'ingreso',
              '41010113': 'ingreso',
              '41010114': 'ingreso',
              '41010115': 'ingreso'
            },
            activo: true,
            created_at: '2025-01-01T00:00:00Z'
          },
          {
            nombre: 'Ventas de Otros Productos',
            grupo: 'Ventas',
            codigos: {
              '41020101': 'ingreso',
              '41020102': 'ingreso',
              '41020103': 'ingreso',
              '41020104': 'ingreso',
              '41020105': 'ingreso'
            },
            activo: true,
            created_at: '2025-01-01T00:00:00Z'
          },
          {
            nombre: 'Fertilizantes',
            grupo: 'Insumos',
            codigos: {
              '61010101': 'costo',
              '61010102': 'costo',
              '61010103': 'costo',
              '61010104': 'costo',
              '61010105': 'costo'
            },
            activo: true,
            created_at: '2025-01-01T00:00:00Z'
          },
          {
            nombre: 'Combustible',
            grupo: 'Insumos',
            codigos: {
              '61020101': 'costo',
              '61020102': 'costo',
              '61020103': 'costo'
            },
            activo: true,
            created_at: '2025-01-01T00:00:00Z'
          }
        ];
        
        // Filtrar por grupo si está seleccionado
        const filteredSubcategories = selectedGroupFilter 
          ? exampleSubcategories.filter(sub => sub.grupo === selectedGroupFilter)
          : exampleSubcategories;
          
        setSubcategories(filteredSubcategories);
      }
    } catch (err: any) {
      console.error('❌ Error general al cargar subcategorías:', err);
      setError(err.response?.data?.message || 'Error al cargar subcategorías financieras');
    } finally {
      setLoading(false);
    }
  };

  const loadExceptions = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      Object.entries(exceptionFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const queryString = params.toString();
      const url = queryString ? `/api/finca-exceptions?${queryString}` : '/api/finca-exceptions';
      const response = await apiService.get<ExceptionsResponse>(url);
      setExceptions(response.data.exceptions);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar excepciones por finca');
    } finally {
      setLoading(false);
    }
  };

  const loadFincas = async (): Promise<void> => {
    try {
      const response = await apiService.get<{ fincas: Array<{ nombre: string }> }>('/api/fincas');
      setFincas(response.data.fincas || []);
    } catch (err: any) {
      console.warn('⚠️ Error al cargar fincas:', err);
      // Si falla, usar las fincas de las excepciones existentes como fallback
      const fincasFromExceptions = Array.from(new Set(exceptions.map(e => e.finca)))
        .filter(f => f)
        .map(f => ({ nombre: f }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre));
      setFincas(fincasFromExceptions);
    }
  };

  // Funciones para Grupos Financieros
  const handleCreateGroup = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await apiService.post('/api/financial-groups', groupForm);
      setSuccess('Grupo financiero creado correctamente');
      setShowGroupModal(false);
      resetGroupForm();
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al crear grupo financiero');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGroup = async (): Promise<void> => {
    if (!editingGroup) return;
    const groupId = editingGroup.id ?? (editingGroup as any).id_padre;
    if (!groupId) {
      setError('El grupo no tiene ID. Recargue la página para obtener los datos actualizados.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await apiService.put(`/api/financial-groups/${groupId}`, groupForm);
      setSuccess('Grupo financiero actualizado correctamente');
      setShowGroupModal(false);
      setEditingGroup(null);
      resetGroupForm();
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al actualizar grupo financiero');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (nombre: string): Promise<void> => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar el grupo "${nombre}"?`)) {
      return;
    }
    const group = groups.find(g => g.nombre === nombre);
    const groupId = group?.id ?? (group as any)?.id_padre;
    if (!groupId) {
      setError('No se pudo obtener el ID del grupo. Recargue la página.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await apiService.delete(`/api/financial-groups/${groupId}`);
      setSuccess('Grupo financiero eliminado correctamente');
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar grupo financiero');
    } finally {
      setLoading(false);
    }
  };

  const handleEditGroup = (group: FinancialGroup): void => {
    setEditingGroup(group);
    setGroupForm({
      nombre: group.nombre,
      orden: group.orden,
      activo: group.activo
    });
    setShowGroupModal(true);
  };

  const resetGroupForm = (): void => {
    setGroupForm({
      nombre: '',
      orden: groups.length + 1,
      activo: true
    });
  };

  // Funciones para gestionar subcategorías desde el modal de grupo
  const handleCreateSubcategoryFromGroup = async (nombre: string, tipoClasificacion: 'costo' | 'gasto' | 'ingreso' = 'costo'): Promise<void> => {
    if (!editingGroup) return;
    const idPadre = editingGroup.id ?? (editingGroup as any).id_padre;
    if (!idPadre) {
      setError('El grupo no tiene ID. Recargue la página para obtener los datos actualizados.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.post('/api/financial-subcategories', {
        nombre,
        id_padre: idPadre,
        tipo_clasificacion: tipoClasificacion,
        codigos: {},
        activo: true
      });

      // Usar la subcategoría devuelta por la API si existe, si no, usar una construida localmente
      const createdSubcategory: FinancialSubcategory = response.data?.subcategory || {
        nombre,
        grupo: groupForm.nombre,
        id_padre: idPadre,
        tipo_clasificacion: tipoClasificacion,
        codigos: {},
        activo: true,
        created_at: new Date().toISOString()
      };

      // Actualización optimista: agregar la nueva subcategoría al estado local
      setSubcategories(prev => [
        ...prev,
        createdSubcategory
      ]);

      setSuccess(`✅ Subcategoría "${nombre}" creada correctamente`);
      // Ya hemos sincronizado el estado local; evitamos llamar inmediatamente a loadSubcategories()
      // para no sobrescribir la actualización optimista con datos aún no actualizados del backend.
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al crear subcategoría');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubcategoryFromGroup = async (
    oldName: string,
    newName: string,
    tipoClasificacion?: 'costo' | 'gasto' | 'ingreso'
  ): Promise<void> => {
    if (!editingGroup) return;
    const sub = subcategories.find(s => s.grupo === editingGroup.nombre && s.nombre === oldName);
    const subId = sub?.id;
    if (!subId) {
      setError('No se pudo obtener el ID de la subcategoría. Recargue la página.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      
      const body: any = { nombre: newName };
      if (tipoClasificacion) body.tipo_clasificacion = tipoClasificacion;
      await apiService.put(`/api/financial-subcategories/${subId}`, body);
      
      setSuccess(`✅ Subcategoría renombrada de "${oldName}" a "${newName}"`);
      await loadSubcategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al actualizar subcategoría');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubcategoryFromGroup = async (nombre: string): Promise<void> => {
    if (!editingGroup) return;
    const sub = subcategories.find(s => s.grupo === editingGroup.nombre && s.nombre === nombre);
    const subId = sub?.id;
    const idPadre = editingGroup.id ?? (editingGroup as any)?.id_padre ?? (sub as any)?.id_padre;

    if (!idPadre) {
      setError('No se pudo obtener el ID del grupo. Recargue la página.');
      return;
    }
    if (!subId) {
      setError('No se pudo obtener el ID de la subcategoría. Recargue la página.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await apiService.delete(`/api/financial-subcategories/${subId}?id_padre=${encodeURIComponent(idPadre)}`);
      setSuccess(`✅ Subcategoría "${nombre}" eliminada. Los códigos se movieron a OTROS`);
      await loadSubcategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar subcategoría');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Funciones para manejar expansión de grupos
  const toggleGroupExpansion = (groupName: string): void => {
    console.log(`🔄 Toggle expansión para grupo: ${groupName}`);
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
        console.log(`📉 Contrayendo grupo: ${groupName}`);
      } else {
        newSet.add(groupName);
        console.log(`📈 Expandiendo grupo: ${groupName}`);
      }
      console.log(`📊 Grupos expandidos actuales:`, Array.from(newSet));
      return newSet;
    });
  };

  const getSubcategoriesForGroup = (groupName: string): FinancialSubcategory[] => {
    return subcategories.filter(sub => sub.grupo === groupName);
  };

  const getCodigoCountForSubcategory = (subcategory: FinancialSubcategory): number => {
    return Object.keys(subcategory.codigos).length;
  };

  // Función para obtener descripciones de códigos usando la nueva API
  const fetchCodigoDescripciones = async (codigos: { [key: string]: string }): Promise<{ [key: string]: string }> => {
    const codigosArray = Object.keys(codigos);
    
    if (codigosArray.length === 0) {
      return {};
    }
    
    try {
      console.log('🔍 Obteniendo descripciones para códigos:', codigosArray);
      
      const response = await apiService.post<{
        codigos: Array<{ codigo: string; descripcion: string }>;
        total_encontrados: number;
        total_solicitados: number;
        codigos_no_encontrados: string[];
        total_no_encontrados: number;
      }>('/api/codigos_descripcion', {
        codigos: codigosArray
      });
      
      const descripciones: { [key: string]: string } = {};
      
      // Procesar códigos encontrados
      response.data.codigos.forEach(item => {
        descripciones[item.codigo] = item.descripcion;
        console.log(`📝 Descripción para código ${item.codigo}:`, item.descripcion);
      });
      
      // Procesar códigos no encontrados con descripción por defecto (si existen)
      if (response.data.codigos_no_encontrados && response.data.codigos_no_encontrados.length > 0) {
        response.data.codigos_no_encontrados.forEach(codigo => {
          const tipo = codigos[codigo];
          descripciones[codigo] = tipo === 'ingreso' ? 'Ingreso operacional' : 
                                 tipo === 'costo' ? 'Costo operacional' : 
                                 'Gasto operacional';
          console.warn(`⚠️ Código ${codigo} no encontrado, usando descripción por defecto:`, descripciones[codigo]);
        });
      }
      
      console.log(`✅ Descripciones obtenidas: ${response.data.total_encontrados}/${response.data.total_solicitados} encontrados`);
      
      return descripciones;
    } catch (error) {
      console.warn('⚠️ Error al obtener descripciones de códigos, usando descripciones por defecto:', error);
      
      // Fallback: usar descripciones por defecto basadas en el tipo
      const descripciones: { [key: string]: string } = {};
      Object.entries(codigos).forEach(([codigo, tipo]) => {
        descripciones[codigo] = tipo === 'ingreso' ? 'Ingreso operacional' : 
                               tipo === 'costo' ? 'Costo operacional' : 
                               'Gasto operacional';
      });
      
      return descripciones;
    }
  };

  // Funciones para Subcategorías Financieras
  const handleCreateSubcategory = async (): Promise<void> => {
    const group = groups.find(g => g.nombre === subcategoryForm.grupo);
    const idPadre = group?.id ?? (group as any)?.id_padre;
    if (!idPadre) {
      setError('Seleccione un grupo con ID válido. Recargue la página si los grupos no muestran ID.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await apiService.post('/api/financial-subcategories', {
        nombre: subcategoryForm.nombre,
        id_padre: idPadre,
        codigos: subcategoryForm.codigos,
        activo: subcategoryForm.activo
      });
      setSuccess('Subcategoría financiera creada correctamente');
      setShowSubcategoryModal(false);
      resetSubcategoryForm();
      loadSubcategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al crear subcategoría financiera');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubcategory = async (): Promise<void> => {
    if (!editingSubcategory) return;
    const subId = editingSubcategory.id;
    if (!subId) {
      setError('No se pudo obtener el ID de la subcategoría. Recargue la página.');
      return;
    }
    const group = groups.find(g => g.nombre === editingSubcategory.grupo);
    const idPadre = group?.id ?? (editingSubcategory as any).id_padre ?? (group as any)?.id_padre;
    try {
      setLoading(true);
      setError(null);
      await apiService.put(`/api/financial-subcategories/${subId}`, {
        nombre: subcategoryForm.nombre,
        ...(idPadre && { id_padre: idPadre }),
        codigos: subcategoryForm.codigos,
        activo: subcategoryForm.activo
      });
      setSuccess('Subcategoría financiera actualizada correctamente');
      setShowSubcategoryModal(false);
      setEditingSubcategory(null);
      resetSubcategoryForm();
      loadSubcategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al actualizar subcategoría financiera');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubcategory = async (nombre: string, grupo: string): Promise<void> => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar la subcategoría "${nombre}"?`)) {
      return;
    }

    const sub = subcategories.find(s => s.nombre === nombre && s.grupo === grupo);
    const subId = sub?.id;
    const group = groups.find(g => g.nombre === grupo);
    const idPadre = group?.id ?? (group as any)?.id_padre ?? (sub as any)?.id_padre;

    if (!idPadre) {
      setError('No se pudo obtener el ID del grupo. Recargue la página.');
      return;
    }
    if (!subId) {
      setError('No se pudo obtener el ID de la subcategoría. Recargue la página.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await apiService.delete(`/api/financial-subcategories/${subId}?id_padre=${encodeURIComponent(idPadre)}`);
      setSuccess('Subcategoría financiera eliminada correctamente');
      loadSubcategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar subcategoría financiera');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubcategory = (subcategory: FinancialSubcategory): void => {
    setEditingSubcategory(subcategory);
    setSubcategoryForm({
      nombre: subcategory.nombre,
      grupo: subcategory.grupo,
      codigos: { ...subcategory.codigos },
      activo: subcategory.activo
    });
    setShowSubcategoryModal(true);
  };

  const resetSubcategoryForm = (): void => {
    setSubcategoryForm({
      nombre: '',
      grupo: groups[0]?.nombre || '',
      codigos: {},
      activo: true
    });
  };

  // Funciones para Excepciones por Finca
  const handleCreateException = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await apiService.post('/api/finca-exceptions', exceptionForm);
      setSuccess('Excepción por finca creada correctamente');
      setShowExceptionModal(false);
      resetExceptionForm();
      loadExceptions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al crear excepción por finca');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateException = async (): Promise<void> => {
    if (!editingException) return;
    
    try {
      setLoading(true);
      setError(null);
      await apiService.put(`/api/finca-exceptions/${editingException.codigo}/${editingException.finca}`, exceptionForm);
      setSuccess('Excepción por finca actualizada correctamente');
      setShowExceptionModal(false);
      setEditingException(null);
      resetExceptionForm();
      loadExceptions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al actualizar excepción por finca');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteException = async (codigo: string, finca: string): Promise<void> => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar la excepción para código "${codigo}" en finca "${finca}"?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await apiService.delete(`/api/finca-exceptions/${codigo}/${finca}`);
      setSuccess('Excepción por finca eliminada correctamente');
      loadExceptions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar excepción por finca');
    } finally {
      setLoading(false);
    }
  };

  const handleEditException = (exception: FincaException): void => {
    setEditingException(exception);
    setExceptionForm({
      codigo: exception.codigo,
      finca: exception.finca,
      subcategoria: exception.subcategoria,
      grupo: exception.grupo,
      clasificacion: exception.clasificacion
    });
    setShowExceptionModal(true);
  };

  const resetExceptionForm = (): void => {
    setExceptionForm({
      codigo: '',
      finca: '',
      subcategoria: '',
      grupo: '',
      clasificacion: 'costo'
    });
  };

  // Funciones auxiliares

  // Función para mostrar códigos de un grupo
  const handleShowGroupCodigos = async (groupName: string): Promise<void> => {
    try {
      setLoadingCodigos(true);
      setError(null);
      
      console.log('🔍 Buscando códigos para el grupo:', groupName);
      
      const group = groups.find(g => g.nombre === groupName);
      const idPadre = group?.id ?? (group as any)?.id_padre;
      
      // Intentar obtener desde la API primero
      try {
        const params = idPadre ? `?id_padre=${encodeURIComponent(idPadre)}` : '';
        const response = await apiService.get<SubcategoriesResponse>(`/api/financial-subcategories${params}`);
        const groupSubcategories = idPadre ? response.data.subcategories : response.data.subcategories.filter((s: FinancialSubcategory) => s.grupo === groupName);
        
        console.log('📋 Subcategorías encontradas:', groupSubcategories);
        
        // Recopilar todos los códigos de las subcategorías
        const allCodigos: { [key: string]: string } = {};
        groupSubcategories.forEach(sub => {
          console.log(`📝 Procesando subcategoría "${sub.nombre}" con códigos:`, sub.codigos);
          Object.entries(sub.codigos).forEach(([codigo, tipo]) => {
            allCodigos[codigo] = tipo;
          });
        });

            console.log('🎯 Total de códigos recopilados:', allCodigos);

            // Obtener descripciones de los códigos
            const descripciones = await fetchCodigoDescripciones(allCodigos);
            setCodigoDescripciones(descripciones);

            setSelectedEntityForCodigos({
              type: 'group',
              name: groupName,
              codigos: allCodigos
            });
            setShowCodigosModal(true);
            setLoadingCodigos(false);
      } catch (apiError) {
        console.warn('⚠️ API no disponible, usando datos de ejemplo');
        
        // Usar datos de ejemplo si la API no está disponible
        const exampleCodigos: { [key: string]: string } = {};
        
        if (groupName === 'Ventas') {
          // Códigos de ejemplo para Ventas
          Object.assign(exampleCodigos, {
            '41010101': 'ingreso', '41010102': 'ingreso', '41010103': 'ingreso',
            '41010104': 'ingreso', '41010105': 'ingreso', '41010106': 'ingreso',
            '41010107': 'ingreso', '41010108': 'ingreso', '41010109': 'ingreso',
            '41010110': 'ingreso', '41010111': 'ingreso', '41010112': 'ingreso',
            '41010113': 'ingreso', '41010114': 'ingreso', '41010115': 'ingreso',
            '41020101': 'ingreso', '41020102': 'ingreso', '41020103': 'ingreso',
            '41020104': 'ingreso', '41020105': 'ingreso'
          });
        } else if (groupName === 'Insumos') {
          // Códigos de ejemplo para Insumos
          Object.assign(exampleCodigos, {
            '61010101': 'costo', '61010102': 'costo', '61010103': 'costo',
            '61010104': 'costo', '61010105': 'costo', '61020101': 'costo',
            '61020102': 'costo', '61020103': 'costo'
          });
        }
        
        console.log('🎯 Códigos de ejemplo para', groupName, ':', exampleCodigos);
        
        // Obtener descripciones de los códigos de ejemplo
        const descripciones = await fetchCodigoDescripciones(exampleCodigos);
        setCodigoDescripciones(descripciones);
        
        setSelectedEntityForCodigos({
          type: 'group',
          name: groupName,
          codigos: exampleCodigos
        });
        setShowCodigosModal(true);
        setLoadingCodigos(false);
      }
    } catch (err: any) {
      console.error('❌ Error al cargar códigos del grupo:', err);
      setError(err.response?.data?.message || 'Error al cargar códigos del grupo');
    } finally {
      setLoadingCodigos(false);
    }
  };

  // Función para mostrar códigos de una subcategoría
  const handleShowSubcategoryCodigos = async (subcategoryName: string, grupo: string): Promise<void> => {
    try {
      setLoadingCodigos(true);
      setError(null);
      
      console.log('🔍 Buscando códigos para la subcategoría:', subcategoryName, 'del grupo:', grupo);
      
      const group = groups.find(g => g.nombre === grupo);
      const idPadre = group?.id ?? (group as any)?.id_padre;
      const params = idPadre ? `?id_padre=${encodeURIComponent(idPadre)}` : '';
      // Obtener la subcategoría específica desde la API
      const response = await apiService.get<SubcategoriesResponse>(`/api/financial-subcategories${params}`);
      const subcategory = response.data.subcategories.find(sub => sub.nombre === subcategoryName);
      
      console.log('📋 Subcategoría encontrada:', subcategory);
      
      if (subcategory) {
        console.log('🎯 Códigos de la subcategoría:', subcategory.codigos);
        const idPadreSub = subcategory.id_padre ?? (group?.id ?? (group as any)?.id_padre);
        
        // Obtener descripciones de los códigos
        const descripciones = await fetchCodigoDescripciones(subcategory.codigos);
        setCodigoDescripciones(descripciones);
        
        setSelectedEntityForCodigos({
          type: 'subcategory',
          name: subcategoryName,
          codigos: subcategory.codigos,
          parentName: grupo,
          id_padre: idPadreSub
        });
        setShowCodigosModal(true);
        setLoadingCodigos(false);
      } else {
        console.warn('⚠️ Subcategoría no encontrada');
        setError('Subcategoría no encontrada');
      }
    } catch (err: any) {
      console.error('❌ Error al cargar códigos de la subcategoría:', err);
      setError(err.response?.data?.message || 'Error al cargar códigos de la subcategoría');
    } finally {
      setLoadingCodigos(false);
    }
  };

  // Función para mostrar códigos de excepciones por finca
  const handleShowExceptionCodigos = async (finca: string): Promise<void> => {
    try {
      setLoadingCodigos(true);
      setError(null);
      
      // Obtener todas las excepciones de esta finca desde la API
      const response = await apiService.get<ExceptionsResponse>(`/api/finca-exceptions?finca=${encodeURIComponent(finca)}`);
      const fincaExceptions = response.data.exceptions;
      
      // Recopilar todos los códigos únicos de las excepciones
      const allCodigos: { [key: string]: string } = {};
      fincaExceptions.forEach(exception => {
        allCodigos[exception.codigo] = exception.clasificacion;
      });

      // Obtener descripciones (solo lectura) para una experiencia consistente
      const descripciones = await fetchCodigoDescripciones(allCodigos);
      setCodigoDescripciones(descripciones);

      setSelectedEntityForCodigos({
        type: 'finca',
        name: finca,
        codigos: allCodigos
      });
      setShowCodigosModal(true);
      setLoadingCodigos(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar códigos de la finca');
    } finally {
      setLoadingCodigos(false);
    }
  };

  const addCodigoToSubcategory = (): void => {
    console.log('🔵 Abriendo modal de agregar código...');
    
    let target: { nombre: string; grupo: string; id_padre?: string } | null = null;
    let defaultTipo: 'costo' | 'gasto' | 'ingreso' = 'costo';
    if (editingSubcategory) {
      const group = groups.find(g => g.nombre === editingSubcategory.grupo);
      const idPadre = editingSubcategory.id_padre ?? (group?.id ?? (group as any)?.id_padre);
      target = {
        nombre: editingSubcategory.nombre,
        grupo: editingSubcategory.grupo,
        id_padre: idPadre
      };
      defaultTipo = editingSubcategory.tipo_clasificacion ?? 'costo';
      console.log('🎯 Subcategoría objetivo (editando):', target);
    } else if (subcategoryForm.nombre && subcategoryForm.grupo) {
      const group = groups.find(g => g.nombre === subcategoryForm.grupo);
      const idPadre = group?.id ?? (group as any)?.id_padre;
      target = {
        nombre: subcategoryForm.nombre,
        grupo: subcategoryForm.grupo,
        id_padre: idPadre
      };
      console.log('🎯 Subcategoría objetivo (formulario):', target);
    } else {
      console.warn('⚠️ No se pudo identificar la subcategoría objetivo');
    }
    
    setTargetSubcategory(target);
    setShowAddCodigoModal(true);
    setAddCodigoForm({ codigo: '', descripcion: '', tipo: defaultTipo });
    setCodigoExistente({ existe: false });
    
    console.log('✅ Modal debería estar abierto ahora');
  };

  // Función para buscar si el código existe
  const handleSearchCodigo = async (codigo: string): Promise<void> => {
    if (!codigo.trim()) {
      setCodigoExistente({ existe: false });
      return;
    }

    try {
      setSearchingCodigo(true);
      console.log('🔍 Iniciando búsqueda del código:', codigo);
      
      // Buscar en todas las subcategorías (el backend exige id_padre; pedir por cada grupo)
      const groupsWithId = groups.filter(g => g.id ?? (g as any)?.id_padre);
      let todasSubcategorias: FinancialSubcategory[] = [];
      if (groupsWithId.length > 0) {
        const results = await Promise.all(
          groupsWithId.map(g => {
            const pid = g.id ?? (g as any)?.id_padre;
            return apiService.get<SubcategoriesResponse>(`/api/financial-subcategories?id_padre=${encodeURIComponent(pid)}`);
          })
        );
        todasSubcategorias = results.flatMap(r => r.data.subcategories || []);
      }
      
      // Buscar el código en todas las subcategorías
      let encontrado = false;
      let infoExistente: any = { existe: false };
      
      for (const sub of todasSubcategorias) {
        if (sub.codigos.hasOwnProperty(codigo)) {
          encontrado = true;
          
          // Obtener descripción del código
          try {
            const descResponse = await apiService.post<{
              codigos: Array<{ codigo: string; descripcion: string }>;
            }>('/api/codigos_descripcion', {
              codigos: [codigo]
            });
            
            const descripcion = descResponse.data.codigos.length > 0 
              ? descResponse.data.codigos[0].descripcion 
              : 'Sin descripción';
            
            infoExistente = {
              existe: true,
              descripcion: descripcion,
              subcategoria: sub.nombre,
              grupo: sub.grupo,
              tipo: sub.codigos[codigo]
            };
            console.log('✅ Código encontrado en:', sub.nombre);
          } catch (error) {
            infoExistente = {
              existe: true,
              descripcion: 'Sin descripción',
              subcategoria: sub.nombre,
              grupo: sub.grupo,
              tipo: sub.codigos[codigo]
            };
          }
          
          break;
        }
      }
      
      setCodigoExistente(infoExistente);
      
      // Si no existe, intentar obtener descripción desde la BD de códigos
      if (!encontrado) {
        console.log('ℹ️ Código no encontrado en subcategorías, buscando descripción...');
        try {
          const descResponse = await apiService.post<{
            codigos: Array<{ codigo: string; descripcion: string }>;
          }>('/api/codigos_descripcion', {
            codigos: [codigo]
          });
          
          if (descResponse.data.codigos.length > 0) {
            setAddCodigoForm(prev => ({
              ...prev,
              descripcion: descResponse.data.codigos[0].descripcion
            }));
            console.log('✅ Descripción encontrada en BD');
          }
        } catch (error) {
          console.log('⚠️ Código no encontrado en BD de descripciones');
        }
      }
    } catch (error) {
      console.error('❌ Error al buscar código:', error);
      setCodigoExistente({ existe: false });
    } finally {
      setSearchingCodigo(false);
      console.log('✅ Búsqueda completada');
    }
  };

  // Función para guardar el código
  const handleSaveCodigoToSubcategory = async (): Promise<void> => {
    if (!addCodigoForm.codigo.trim()) {
      setError('Debe ingresar un código');
      return;
    }

    if (!targetSubcategory) {
      setError('No se pudo identificar la subcategoría destino');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (codigoExistente.existe) {
        // Mover código de una subcategoría a otra usando el endpoint /api/financial-subcategories/codes/move
        if (codigoExistente.subcategoria && codigoExistente.grupo) {
          console.log('🔄 Moviendo código usando endpoint de mover...');
          const origenGroup = groups.find(g => g.nombre === codigoExistente.grupo);
          const origenIdPadre = origenGroup?.id ?? (origenGroup as any)?.id_padre;
          const destinoIdPadre = targetSubcategory.id_padre ?? (groups.find(g => g.nombre === targetSubcategory.grupo)?.id ?? (groups.find(g => g.nombre === targetSubcategory.grupo) as any)?.id_padre);
          
          const moveResponse = await apiService.put('/api/financial-subcategories/codes/move', {
            codigo: addCodigoForm.codigo,
            origen_subcategoria: codigoExistente.subcategoria,
            origen_id_padre: origenIdPadre,
            destino_subcategoria: targetSubcategory.nombre,
            destino_id_padre: destinoIdPadre,
            nueva_clasificacion: codigoExistente.tipo || 'costo'
          });

          console.log('✅ Respuesta del servidor:', moveResponse.data);
          setSuccess(moveResponse.data.message || `✅ Código ${addCodigoForm.codigo} movido de "${codigoExistente.subcategoria}" → "${targetSubcategory.nombre}"`);
          
          // Recargar subcategorías para reflejar cambios
          await loadSubcategories();
        }
      } else {
        // Agregar nuevo código usando el endpoint /api/financial-subcategories/codes
        if (!addCodigoForm.descripcion.trim()) {
          setError('Debe ingresar una descripción para el nuevo código');
          return;
        }

        if (!addCodigoForm.tipo) {
          setError('Debe seleccionar un tipo para el nuevo código');
          return;
        }

        console.log('➕ Agregando nuevo código...');
        
        // Paso 1: Crear la descripción del código en la base de datos
        try {
          console.log('📝 Paso 1: Creando descripción del código en BD...');
          console.log('📤 Enviando a /api/codigos-nombres:', {
            codigo: addCodigoForm.codigo,
            descripcion: addCodigoForm.descripcion
          });
          
          const createDescResponse = await apiService.post('/api/codigos-nombres', {
            codigo: addCodigoForm.codigo,
            descripcion: addCodigoForm.descripcion
          });
          
          console.log('✅ Descripción creada exitosamente:', createDescResponse.data);
        } catch (descError: any) {
          console.error('❌ Error al crear descripción:', descError);
          console.error('📊 Detalles del error:', {
            status: descError.response?.status,
            statusText: descError.response?.statusText,
            data: descError.response?.data,
            message: descError.message,
            config: {
              url: descError.config?.url,
              method: descError.config?.method,
              data: descError.config?.data
            }
          });
          
          // Si el código ya existe en la BD de descripciones, está bien, continuamos
          if (descError.response?.status === 400 && descError.response?.data?.message?.includes('Ya existe')) {
            console.log('ℹ️ La descripción del código ya existe en BD, continuando...');
          } else if (descError.response?.status === 404 || descError.message?.includes('Network Error') || descError.message?.includes('CORS')) {
            // Si el endpoint no existe o hay problema de CORS, mostrar advertencia pero continuar
            console.warn('⚠️ No se pudo crear la descripción en BD (endpoint puede no estar disponible), continuando sin guardar descripción...');
          } else {
            // Para otros errores, mostrar advertencia pero continuar
            console.warn('⚠️ Error al guardar descripción, pero continuando con la operación...');
          }
        }
        
        // Paso 2: Agregar el código a la subcategoría
        console.log('📂 Agregando código a la subcategoría...');
        const addBody: { subcategoria: string; grupo?: string; id_padre?: string; codigo: string; clasificacion: string } = {
          subcategoria: targetSubcategory.nombre,
          codigo: addCodigoForm.codigo,
          clasificacion: addCodigoForm.tipo
        };
        if (targetSubcategory.id_padre) addBody.id_padre = targetSubcategory.id_padre;
        const addResponse = await apiService.post('/api/financial-subcategories/codes', addBody);

        console.log('✅ Respuesta del servidor:', addResponse.data);
        setSuccess(addResponse.data.message || `✅ Código ${addCodigoForm.codigo} agregado a "${targetSubcategory.nombre}" con descripción guardada en BD`);
        
        // Recargar subcategorías para reflejar cambios
        await loadSubcategories();
      }

      // Cerrar modal y limpiar
      setShowAddCodigoModal(false);
      setAddCodigoForm({ codigo: '', descripcion: '', tipo: 'costo' });
      setCodigoExistente({ existe: false });
      
      // Si estamos en el modal de códigos, cerrarlo y reabrir para ver los cambios
      if (showCodigosModal && selectedEntityForCodigos) {
        setShowCodigosModal(false);
        // Pequeño delay para que el usuario vea el mensaje de éxito
        setTimeout(() => {
          // Reabrir el modal de códigos actualizado
          if (selectedEntityForCodigos.type === 'subcategory') {
            handleShowSubcategoryCodigos(selectedEntityForCodigos.name, targetSubcategory.grupo);
          }
        }, 1500);
      }
    } catch (err: any) {
      console.error('❌ Error al guardar código:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Error al guardar el código';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const removeCodigoFromSubcategory = (codigo: string): void => {
    setSubcategoryForm(prev => {
      const newCodigos = { ...prev.codigos };
      delete newCodigos[codigo];
      return {
        ...prev,
        codigos: newCodigos
      };
    });
  };

  // Funciones para editar códigos
  const handleEditCodigos = (): void => {
    if (selectedEntityForCodigos) {
      setEditingCodigos({ ...selectedEntityForCodigos.codigos });
      setShowEditCodigosModal(true);
    }
  };

  const handleSaveCodigos = async (): Promise<void> => {
    if (!selectedEntityForCodigos) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Aquí implementarías la lógica para guardar los códigos
      // Por ahora solo actualizamos el estado local
      setSelectedEntityForCodigos(prev => prev ? {
        ...prev,
        codigos: editingCodigos
      } : null);
      
      setSuccess('Códigos actualizados correctamente');
      setShowEditCodigosModal(false);
      
      // Recargar datos para reflejar cambios
      if (selectedEntityForCodigos.type === 'group') {
        await loadSubcategories();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar códigos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCodigo = (): void => {
    const codigo = prompt('Ingrese el código:');
    const tipo = prompt('Ingrese el tipo (costo/gasto/ingreso):');
    
    if (codigo && tipo && ['costo', 'gasto', 'ingreso'].includes(tipo)) {
      setEditingCodigos(prev => ({
        ...prev,
        [codigo]: tipo
      }));
    }
  };

  const handleRemoveCodigo = (codigo: string): void => {
    setEditingCodigos(prev => {
      const newCodigos = { ...prev };
      delete newCodigos[codigo];
      return newCodigos;
    });
  };

  // Limpiar mensajes después de un tiempo
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  if (loading && groups.length === 0) {
    return (
      <div className="administrador-codigos-container">
        <div className="loading-container">
          <div className="loading-spinner">
            <img src="/logocabeza.svg" alt="Logo" />
          </div>
          <p>Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="administrador-codigos-container">
      <Helmet>
        <title>Administrador de Códigos - Ganando</title>
        <meta name="description" content="Administra grupos financieros, subcategorías y excepciones por finca" />
      </Helmet>

      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <div className="admin-title-section">
            <h1 className="admin-title">Administrador de Códigos</h1>
            <p className="admin-subtitle">Gestiona grupos financieros, subcategorías y excepciones por finca</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          Grupos Financieros
        </button>
        <button
          className={`tab-button ${activeTab === 'exceptions' ? 'active' : ''}`}
          onClick={() => setActiveTab('exceptions')}
        >
          Excepciones por Finca
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="error-message">
          <svg className="error-icon" viewBox="0 0 1024 1024">
            <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm-32 232c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v272c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V296zm32 440a48.01 48.01 0 0 1 0-96 48.01 48.01 0 0 1 0 96z"></path>
          </svg>
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          <svg className="success-icon" viewBox="0 0 1024 1024">
            <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm193.5 301.7l-210.6 292a31.8 31.8 0 0 1-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.2 0 19.9 4.9 25.9 13.3l71.2 98.8 157.2-218c6-8.3 15.6-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.5 12.7z"></path>
          </svg>
          {success}
        </div>
      )}

      {/* Content based on active tab */}
      {activeTab === 'groups' && (
        <GroupsTab
          groups={groups}
          subcategories={subcategories}
          loading={loading}
          expandedGroups={expandedGroups}
          onEdit={handleEditGroup}
          onDelete={handleDeleteGroup}
          onAdd={() => {
            resetGroupForm();
            setEditingGroup(null);
            setShowGroupModal(true);
          }}
          onToggleExpansion={toggleGroupExpansion}
          onShowSubcategoryCodigos={handleShowSubcategoryCodigos}
        />
      )}

      {activeTab === 'exceptions' && (
        <ExceptionsTab
          exceptions={exceptions}
          groups={groups}
          subcategories={subcategories}
          loading={loading}
          filters={exceptionFilters}
          onFilterChange={setExceptionFilters}
          onEdit={handleEditException}
          onDelete={handleDeleteException}
          onAdd={() => {
            resetExceptionForm();
            setEditingException(null);
            setShowExceptionModal(true);
          }}
          onRefresh={loadExceptions}
          onShowCodigos={handleShowExceptionCodigos}
        />
      )}

      {/* Modals */}
      {showGroupModal && (
        <GroupModal
          group={editingGroup}
          form={groupForm}
          subcategories={subcategories}
          onFormChange={setGroupForm}
          onSave={editingGroup ? handleUpdateGroup : handleCreateGroup}
          onClose={() => setShowGroupModal(false)}
          onCreateSubcategory={handleCreateSubcategoryFromGroup}
          onUpdateSubcategory={handleUpdateSubcategoryFromGroup}
          onDeleteSubcategory={handleDeleteSubcategoryFromGroup}
          loading={loading}
        />
      )}

      {showSubcategoryModal && (
        <SubcategoryModal
          subcategory={editingSubcategory}
          form={subcategoryForm}
          groups={groups}
          onFormChange={setSubcategoryForm}
          onSave={editingSubcategory ? handleUpdateSubcategory : handleCreateSubcategory}
          onClose={() => setShowSubcategoryModal(false)}
          onAddCodigo={addCodigoToSubcategory}
          onRemoveCodigo={removeCodigoFromSubcategory}
          loading={loading}
        />
      )}

      {showExceptionModal && (
        <ExceptionModal
          exception={editingException}
          form={exceptionForm}
          groups={groups}
          subcategories={subcategories}
          fincas={fincas}
          onFormChange={setExceptionForm}
          onSave={editingException ? handleUpdateException : handleCreateException}
          onClose={() => setShowExceptionModal(false)}
          loading={loading}
        />
      )}

      {/* Modal para mostrar códigos */}
      {showCodigosModal && selectedEntityForCodigos && (
        <CodigosModal
          entity={selectedEntityForCodigos}
          descripciones={codigoDescripciones}
          onClose={() => setShowCodigosModal(false)}
          onAddCodigo={() => {
            console.log('🟢 Abriendo modal desde CodigosModal');
            // Si es una subcategoría, configurar el target
            if (selectedEntityForCodigos.type === 'subcategory') {
              // Buscar la subcategoría (incluye id_padre y tipo_clasificacion)
              const subcategoria = subcategories.find(
                sub => sub.nombre === selectedEntityForCodigos.name
              );
              const group = groups.find(g => g.nombre === subcategoria?.grupo);
              const idPadre = subcategoria?.id_padre ?? (group?.id ?? (group as any)?.id_padre);
              
              if (subcategoria) {
                setTargetSubcategory({
                  nombre: subcategoria.nombre,
                  grupo: subcategoria.grupo,
                  id_padre: idPadre
                });
                setShowAddCodigoModal(true);
                setAddCodigoForm({
                  codigo: '',
                  descripcion: '',
                  tipo: subcategoria.tipo_clasificacion ?? 'costo'
                });
                setCodigoExistente({ existe: false });
                console.log('✅ Modal configurado para:', subcategoria);
              }
            }
          }}
        />
      )}
      
      {/* Loading Overlay: guardar, eliminar o cargar información */}
      <LoadingOverlay isLoading={loading || loadingCodigos} />

      {/* Modal para agregar código avanzado */}
      {(() => {
        console.log('🔍 Verificando modal:', {
          showAddCodigoModal,
          targetSubcategory,
          deberiaRenderizar: showAddCodigoModal && targetSubcategory
        });
        
        if (showAddCodigoModal && targetSubcategory) {
          console.log('✅ Renderizando AddCodigoModalAdvanced');
          return (
            <AddCodigoModalAdvanced
              targetSubcategory={targetSubcategory}
              form={addCodigoForm}
              codigoExistente={codigoExistente}
              onFormChange={setAddCodigoForm}
              onSearchCodigo={handleSearchCodigo}
              onSave={handleSaveCodigoToSubcategory}
              onClose={() => {
                console.log('🚪 Cerrando modal');
                setShowAddCodigoModal(false);
                setAddCodigoForm({ codigo: '', descripcion: '', tipo: 'costo' });
                setCodigoExistente({ existe: false });
              }}
              loading={loading}
              searchingCodigo={searchingCodigo}
            />
          );
        }
        return null;
      })()}
    </div>
  );
};

// Componentes para cada tab
interface GroupsTabProps {
  groups: FinancialGroup[];
  subcategories: FinancialSubcategory[];
  loading: boolean;
  expandedGroups: Set<string>;
  onEdit: (group: FinancialGroup) => void;
  onDelete: (nombre: string) => void;
  onAdd: () => void;
  onToggleExpansion: (groupName: string) => void;
  onShowSubcategoryCodigos: (subcategoryName: string, grupo: string) => void;
}

const GroupsTab: React.FC<GroupsTabProps> = ({ 
  groups, 
  subcategories, 
  loading, 
  expandedGroups, 
  onEdit, 
  onDelete, 
  onAdd, 
  onToggleExpansion, 
  onShowSubcategoryCodigos 
}) => (
  <div className="tab-content">
    <div className="tab-header">
      <h2>Grupos Financieros</h2>
      <button className="add-button" onClick={onAdd} disabled={loading}>
        <svg className="add-icon" viewBox="0 0 1024 1024">
          <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm192 472c0 4.4-3.6 8-8 8H544v152c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V544H328c-4.4 0-8-3.6-8-8v-48c0-4.4 3.6-8 8-8h152V328c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v152h152c4.4 0 8 3.6 8 8v48z"></path>
        </svg>
        Agregar Grupo
      </button>
    </div>

    <div className="admin-table-container">
      <div className="admin-table-header">
        <div className="admin-table-header-cell">Categoría</div>
        <div className="admin-table-header-cell">Subcategoría</div>
        <div className="admin-table-header-cell">Cantidad de Códigos</div>
        <div className="admin-table-header-cell">Acciones</div>
      </div>

      <div className="admin-table-body">
        {groups.length === 0 ? (
          <div className="no-results">
            <p>No hay grupos financieros</p>
          </div>
        ) : (
          groups.map((group, index) => {
            const isExpanded = expandedGroups.has(group.nombre);
            const groupSubcategories = subcategories.filter(sub => sub.grupo === group.nombre);
            
            // Debug logs
            console.log(`🔍 Grupo: ${group.nombre}, Expandido: ${isExpanded}, Subcategorías: ${groupSubcategories.length}`);
            if (groupSubcategories.length > 0) {
              console.log(`📋 Subcategorías para ${group.nombre}:`, groupSubcategories.map(s => s.nombre));
            }
            
            return (
              <React.Fragment key={index}>
                {/* Fila del Grupo */}
                <div className="admin-table-row group-row clickable-group-row" onClick={() => onToggleExpansion(group.nombre)}>
                  <div className="admin-table-cell" data-label="Categoría">
                    <div className="group-header">
                      <div 
                        className="expand-button"
                        title={isExpanded ? "Contraer" : "Expandir"}
                      >
                        <svg 
                          className={`expand-icon ${isExpanded ? 'expanded' : ''}`} 
                          viewBox="0 0 1024 1024"
                        >
                          <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7L487.8 752c12.1 16.2 35.2 16.2 47.3 0l360.8-483.3c3.8-5.3.1-12.7-6.4-12.7z"></path>
                        </svg>
                      </div>
                      <span className="group-name">{group.nombre}</span>
                    </div>
                  </div>
                  <div className="admin-table-cell" data-label="Subcategoría">
                    <span className="group-subtitle">Grupo Principal</span>
                  </div>
                  <div className="admin-table-cell" data-label="Cantidad de Códigos">
                    <span className="codigo-count">
                      {groupSubcategories.reduce((total, sub) => total + Object.keys(sub.codigos).length, 0)} códigos
                    </span>
                  </div>
                  <div className="admin-table-cell" data-label="Acciones">
                    <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="action-button edit-button"
                        onClick={() => onEdit(group)}
                        title="Editar grupo"
                        disabled={loading}
                      >
                        <svg className="action-icon" viewBox="0 0 1024 1024">
                          <path d="M257.7 752c2 0 4-.2 6-.5L431.9 722c2-.4 3.9-1.3 5.3-2.8l423.9-423.9a9.96 9.96 0 0 0 0-14.1L694.9 114.9c-1.9-1.9-4.4-2.9-7.1-2.9s-5.2 1-7.1 2.9L256.8 538.8c-1.5 1.5-2.4 3.3-2.8 5.3l-29.5 168.2a33.5 33.5 0 0 0 9.4 29.8c6.6 6.4 14.9 9.9 23.8 9.9zm67.4-174.4L687.8 215l73.3 73.3-362.7 362.6-88.9 15.7 15.6-89zM880 836H144c-17.7 0-32 14.3-32 32v36c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-36c0-17.7-14.3-32-32-32z"></path>
                        </svg>
                      </button>
                      <button
                        className="action-button delete-button"
                        onClick={() => onDelete(group.nombre)}
                        title="Eliminar grupo"
                        disabled={loading}
                      >
                        <svg className="action-icon" viewBox="0 0 1024 1024">
                          <path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.1 0 62.3-26.9 63.9-61l24.7-523H896c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Subcategorías expandidas */}
                {isExpanded && groupSubcategories.map((subcategory, subIndex) => (
                  <div 
                    key={`${index}-${subIndex}`} 
                    className="admin-table-row subcategory-row clickable-subcategory-row"
                    onClick={() => onShowSubcategoryCodigos(subcategory.nombre, subcategory.grupo)}
                    title="Hacer clic para ver códigos de esta subcategoría"
                  >
                    <div className="admin-table-cell" data-label="Categoría">
                      <span className="subcategory-indent">└─</span>
                    </div>
                    <div className="admin-table-cell" data-label="Subcategoría">
                      <span className="subcategory-name clickable-name">
                        {subcategory.nombre}
                      </span>
                    </div>
                    <div className="admin-table-cell" data-label="Cantidad de Códigos">
                      <span className="codigo-count">
                        ({Object.keys(subcategory.codigos).length})
                      </span>
                    </div>
                    <div className="admin-table-cell" data-label="Acciones">
                      <span className="subcategory-actions clickable-action">Ver códigos</span>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            );
          })
        )}
      </div>
    </div>
  </div>
);

// Componente para Excepciones por Finca
interface ExceptionsTabProps {
  exceptions: FincaException[];
  groups: FinancialGroup[];
  subcategories: FinancialSubcategory[];
  loading: boolean;
  filters: {
    finca: string;
    grupo: string;
    subcategoria: string;
    codigo: string;
  };
  onFilterChange: (filters: any) => void;
  onEdit: (exception: FincaException) => void;
  onDelete: (codigo: string, finca: string) => void;
  onAdd: () => void;
  onRefresh: () => void;
  onShowCodigos: (finca: string) => void;
}

const ExceptionsTab: React.FC<ExceptionsTabProps> = ({
  exceptions,
  groups,
  subcategories,
  loading,
  filters,
  onFilterChange,
  onEdit,
  onDelete,
  onAdd,
  onRefresh,
  onShowCodigos
}) => {
  const fincaOptions = Array.from(new Set(exceptions.map(e => e.finca))).sort();
  return (
  <div className="tab-content">
    <div className="tab-header">
      <h2>Excepciones por Finca</h2>
      <div className="tab-controls">
        <div className="filters-row">
          <select
            value={filters.finca}
            onChange={(e) => onFilterChange({ ...filters, finca: e.target.value })}
            className="filter-select filter-half"
          >
            <option value="">Fincas</option>
            {fincaOptions.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Código"
            value={filters.codigo}
            onChange={(e) => onFilterChange({ ...filters, codigo: e.target.value })}
            className="filter-input filter-half"
          />
          <select
            value={filters.grupo}
            onChange={(e) => onFilterChange({ ...filters, grupo: e.target.value })}
            className="filter-select"
          >
            <option value="">Todos los grupos</option>
            {groups.map(group => (
              <option key={group.nombre} value={group.nombre}>
                {group.nombre}
              </option>
            ))}
          </select>
          <select
            value={filters.subcategoria}
            onChange={(e) => onFilterChange({ ...filters, subcategoria: e.target.value })}
            className="filter-select"
          >
            <option value="">Todas las subcategorías</option>
            {subcategories.map(sub => (
              <option key={`${sub.grupo}-${sub.nombre}`} value={sub.nombre}>
                {sub.nombre}
              </option>
            ))}
          </select>
        </div>
        <button className="refresh-button" onClick={onRefresh} disabled={loading}>
          <svg className="refresh-icon" viewBox="0 0 1024 1024">
            <path d="M909.1 209.3l-56.4 44.1C775.8 155.1 656.2 92 521.9 92 290 92 102.3 279.6 102.3 511.5S290 931 521.9 931c164.9 0 307.8-88.1 384.2-218.1l-56.4-44.1c-66.1 108.2-184.1 177.1-327.8 177.1-204.7 0-370.7-166-370.7-370.7s166-370.7 370.7-370.7c118.1 0 220.1 55.1 287.8 135.7l-56.4 44.1L909.1 209.3z"></path>
          </svg>
        </button>
        <button className="add-button" onClick={onAdd} disabled={loading}>
          <svg className="add-icon" viewBox="0 0 1024 1024">
            <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm192 472c0 4.4-3.6 8-8 8H544v152c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V544H328c-4.4 0-8-3.6-8-8v-48c0-4.4 3.6-8 8-8h152V328c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v152h152c4.4 0 8 3.6 8 8v48z"></path>
          </svg>
          Agregar Excepción
        </button>
      </div>
    </div>

    <div className="admin-table-container">
      <div className="exceptions-table-header">
        <div className="admin-table-header-cell">Código</div>
        <div className="admin-table-header-cell">Finca</div>
        <div className="admin-table-header-cell">Subcategoría</div>
        <div className="admin-table-header-cell">Grupo</div>
        <div className="admin-table-header-cell">Clasificación</div>
        <div className="admin-table-header-cell">Acciones</div>
      </div>

      <div className="admin-table-body">
        {exceptions.length === 0 ? (
          <div className="no-results">
            <p>No hay excepciones por finca</p>
          </div>
        ) : (
          exceptions.map((exception, index) => (
            <div key={index} className="exceptions-table-row">
              <div className="admin-table-cell" data-label="Código">
                <span className="codigo-text">{exception.codigo}</span>
              </div>
              <div className="admin-table-cell" data-label="Finca">
                <span 
                  className="finca-badge clickable-name" 
                  onClick={() => onShowCodigos(exception.finca)}
                  title="Hacer clic para ver códigos relacionados"
                >
                  {exception.finca}
                </span>
              </div>
              <div className="admin-table-cell" data-label="Subcategoría">
                <span className="subcategoria-text">{exception.subcategoria}</span>
              </div>
              <div className="admin-table-cell" data-label="Grupo">
                <span className="group-badge">{exception.grupo}</span>
              </div>
              <div className="admin-table-cell" data-label="Clasificación">
                <span 
                  className="clasificacion-badge"
                  style={{ backgroundColor: getClasificacionColor(exception.clasificacion) }}
                >
                  {exception.clasificacion}
                </span>
              </div>
              <div className="admin-table-cell" data-label="Acciones">
                <div className="action-buttons">
                  <button
                    className="action-button edit-button"
                    onClick={() => onEdit(exception)}
                    title="Editar excepción"
                    disabled={loading}
                  >
                    <svg className="action-icon" viewBox="0 0 1024 1024">
                      <path d="M257.7 752c2 0 4-.2 6-.5L431.9 722c2-.4 3.9-1.3 5.3-2.8l423.9-423.9a9.96 9.96 0 0 0 0-14.1L694.9 114.9c-1.9-1.9-4.4-2.9-7.1-2.9s-5.2 1-7.1 2.9L256.8 538.8c-1.5 1.5-2.4 3.3-2.8 5.3l-29.5 168.2a33.5 33.5 0 0 0 9.4 29.8c6.6 6.4 14.9 9.9 23.8 9.9zm67.4-174.4L687.8 215l73.3 73.3-362.7 362.6-88.9 15.7 15.6-89zM880 836H144c-17.7 0-32 14.3-32 32v36c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-36c0-17.7-14.3-32-32-32z"></path>
                    </svg>
                  </button>
                  <button
                    className="action-button delete-button"
                    onClick={() => onDelete(exception.codigo, exception.finca)}
                    title="Eliminar excepción"
                    disabled={loading}
                  >
                    <svg className="action-icon" viewBox="0 0 1024 1024">
                      <path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.1 0 62.3-26.9 63.9-61l24.7-523H896c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  </div>
  );
}

// Componentes de Modales
interface GroupModalProps {
  group: FinancialGroup | null;
  form: {
    nombre: string;
    orden: number;
    activo: boolean;
  };
  subcategories: FinancialSubcategory[];
  onFormChange: (form: any) => void;
  onSave: () => void;
  onClose: () => void;
  onCreateSubcategory: (nombre: string, tipoClasificacion: 'costo' | 'gasto' | 'ingreso') => Promise<void>;
  onUpdateSubcategory: (
    oldName: string,
    newName: string,
    tipoClasificacion?: 'costo' | 'gasto' | 'ingreso'
  ) => Promise<void>;
  onDeleteSubcategory: (nombre: string) => Promise<void>;
  loading: boolean;
}

const GroupModal: React.FC<GroupModalProps> = ({ 
  group, 
  form, 
  subcategories,
  onFormChange, 
  onSave, 
  onClose, 
  onCreateSubcategory,
  onUpdateSubcategory,
  onDeleteSubcategory,
  loading 
}) => {
  const [showAddSubcategory, setShowAddSubcategory] = React.useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = React.useState('');
  const [newSubcategoryClasificacion, setNewSubcategoryClasificacion] = React.useState<'costo' | 'gasto' | 'ingreso'>('costo');
  const [editingSubcategory, setEditingSubcategory] = React.useState<string | null>(null);
  const [editSubcategoryName, setEditSubcategoryName] = React.useState('');
  const [editSubcategoryClasificacion, setEditSubcategoryClasificacion] = React.useState<'costo' | 'gasto' | 'ingreso'>('costo');
  
  // Filtrar subcategorías de este grupo
  const groupSubcategories = group ? subcategories.filter(sub => sub.grupo === form.nombre) : [];

  const handleCreateSubcategory = async () => {
    if (!newSubcategoryName.trim()) return;
    
    try {
      await onCreateSubcategory(newSubcategoryName.trim(), newSubcategoryClasificacion);
      setNewSubcategoryName('');
      setNewSubcategoryClasificacion('costo');
      setShowAddSubcategory(false);
    } catch (error) {
      console.error('Error al crear subcategoría:', error);
    }
  };

  const handleUpdateSubcategory = async (oldName: string) => {
    const trimmed = editSubcategoryName.trim();
    const didChangeName = trimmed && trimmed !== oldName;
    const current = groupSubcategories.find(s => s.nombre === oldName);
    const didChangeClasificacion =
      !!editSubcategoryClasificacion &&
      editSubcategoryClasificacion !== (current?.tipo_clasificacion ?? 'costo');

    if (!didChangeName && !didChangeClasificacion) {
      setEditingSubcategory(null);
      setEditSubcategoryName('');
      return;
    }
    
    try {
      await onUpdateSubcategory(
        oldName,
        didChangeName ? trimmed : oldName,
        editSubcategoryClasificacion
      );
      setEditingSubcategory(null);
      setEditSubcategoryName('');
    } catch (error) {
      console.error('Error al actualizar subcategoría:', error);
    }
  };

  const handleDeleteSubcategory = async (nombre: string) => {
    const confirmMessage = `Si procede a eliminar la subcategoría "${nombre}", los códigos que esta contenía pasarán a OTROS. ¿Desea proseguir?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await onDeleteSubcategory(nombre);
      } catch (error) {
        console.error('Error al eliminar subcategoría:', error);
      }
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !loading && onClose()}>
      <div className="modal-content group-modal-extended" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {group ? 'Editar Grupo Financiero' : 'Agregar Grupo Financiero'}
          </h2>
          <button className="modal-close" onClick={onClose} disabled={loading}>
            <svg viewBox="0 0 1024 1024">
              <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 0 0 203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"></path>
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <form className="form" onSubmit={(e) => { e.preventDefault(); onSave(); }}>
            <div className="form-group">
              <label className="form-label">Nombre del Grupo</label>
              <input
                type="text"
                value={form.nombre}
                onChange={(e) => onFormChange({ ...form, nombre: e.target.value })}
                className="form-input"
                placeholder="Ej: Ventas, Insumos, Gastos"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Posición</label>
              <input
                type="number"
                value={form.orden}
                onChange={(e) => onFormChange({ ...form, orden: parseInt(e.target.value) })}
                className="form-input"
                min="1"
                required
              />
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={onClose} disabled={loading}>
                Cancelar
              </button>
              <button type="submit" className="save-button" disabled={loading}>
                {loading ? 'Guardando...' : (group ? 'Actualizar' : 'Guardar')}
              </button>
            </div>
          </form>

          {/* Sección de Subcategorías (solo si estamos editando un grupo existente) */}
          {group && (
            <div className="subcategories-section">
              <div className="section-header">
                <h3 className="section-title">Subcategorías de este Grupo</h3>
                <button
                  type="button"
                  className="add-button"
                  onClick={() => setShowAddSubcategory(!showAddSubcategory)}
                  disabled={loading}
                >
                  <svg className="add-icon" viewBox="0 0 1024 1024">
                    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm192 472c0 4.4-3.6 8-8 8H544v152c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V544H328c-4.4 0-8-3.6-8-8v-48c0-4.4 3.6-8 8-8h152V328c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v152h152c4.4 0 8 3.6 8 8v48z"></path>
                  </svg>
                  {showAddSubcategory ? 'Cancelar' : 'Agregar Subcategoría'}
                </button>
              </div>

              {/* Formulario para agregar nueva subcategoría */}
              {showAddSubcategory && (
                <div className="add-subcategory-form">
                  <input
                    type="text"
                    value={newSubcategoryName}
                    onChange={(e) => setNewSubcategoryName(e.target.value)}
                    className="form-input"
                    placeholder="Nombre de la nueva subcategoría"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreateSubcategory();
                      }
                    }}
                  />
                  <label className="form-label add-subcategory-clasificacion-label">Clasificación</label>
                  <select
                    value={newSubcategoryClasificacion}
                    onChange={(e) => setNewSubcategoryClasificacion(e.target.value as 'costo' | 'gasto' | 'ingreso')}
                    className="form-input add-subcategory-clasificacion-select"
                  >
                    <option value="costo">Costo</option>
                    <option value="gasto">Gasto</option>
                    <option value="ingreso">Ingreso</option>
                  </select>
                  <button
                    type="button"
                    className="btn-boton4 btn-save"
                    onClick={handleCreateSubcategory}
                    disabled={!newSubcategoryName.trim() || loading}
                  >
                    Crear
                  </button>
                </div>
              )}

              {/* Lista de subcategorías */}
              <div className="subcategories-list">
                {groupSubcategories.length === 0 ? (
                  <p className="no-subcategories">No hay subcategorías en este grupo</p>
                ) : (
                  groupSubcategories.map((sub, index) => (
                    <div key={index} className="subcategory-item">
                      {editingSubcategory === sub.nombre ? (
                        <>
                          <input
                            type="text"
                            value={editSubcategoryName}
                            onChange={(e) => setEditSubcategoryName(e.target.value)}
                            className="form-input subcategory-edit-input"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleUpdateSubcategory(sub.nombre);
                              } else if (e.key === 'Escape') {
                                setEditingSubcategory(null);
                              }
                            }}
                            autoFocus
                          />
                          <select
                            value={editSubcategoryClasificacion}
                            onChange={(e) => setEditSubcategoryClasificacion(e.target.value as 'costo' | 'gasto' | 'ingreso')}
                            className="form-input subcategory-edit-clasificacion-select"
                            title="Clasificación"
                          >
                            <option value="costo">Costo</option>
                            <option value="gasto">Gasto</option>
                            <option value="ingreso">Ingreso</option>
                          </select>
                          <div className="subcategory-actions">
                            <button
                              type="button"
                              className="action-button edit-button"
                              onClick={() => handleUpdateSubcategory(sub.nombre)}
                              title="Guardar"
                              disabled={loading}
                            >
                              <svg className="action-icon" viewBox="0 0 1024 1024">
                                <path d="M912 190h-69.9c-9.8 0-19.1 4.5-25.1 12.2L404.7 724.5 207 474a32 32 0 0 0-25.1-12.2H112c-6.7 0-10.4 7.7-6.3 12.9l273.9 347c12.8 16.2 37.4 16.2 50.3 0l488.4-618.9c4.1-5.1.4-12.8-6.3-12.8z"></path>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="action-button delete-button"
                              onClick={() => setEditingSubcategory(null)}
                              title="Cancelar"
                              disabled={loading}
                            >
                              <svg className="action-icon" viewBox="0 0 1024 1024">
                                <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 0 0 203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"></path>
                              </svg>
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="subcategory-name">{sub.nombre}</span>
                          {sub.tipo_clasificacion && (
                            <span
                              className="clasificacion-badge subcategory-clasificacion-badge"
                              style={{ backgroundColor: getClasificacionColor(sub.tipo_clasificacion) }}
                              title={`Clasificación: ${sub.tipo_clasificacion}`}
                            >
                              {sub.tipo_clasificacion}
                            </span>
                          )}
                          <span className="subcategory-count">
                            ({Object.keys(sub.codigos).length} códigos)
                          </span>
                          <div className="subcategory-actions">
                            <button
                              type="button"
                              className="action-button edit-button"
                              onClick={() => {
                                setEditingSubcategory(sub.nombre);
                                setEditSubcategoryName(sub.nombre);
                                setEditSubcategoryClasificacion(sub.tipo_clasificacion ?? 'costo');
                              }}
                              title="Editar nombre"
                              disabled={loading}
                            >
                              <svg className="action-icon" viewBox="0 0 1024 1024">
                                <path d="M257.7 752c2 0 4-.2 6-.5L431.9 722c2-.4 3.9-1.3 5.3-2.8l423.9-423.9a9.96 9.96 0 0 0 0-14.1L694.9 114.9c-1.9-1.9-4.4-2.9-7.1-2.9s-5.2 1-7.1 2.9L256.8 538.8c-1.5 1.5-2.4 3.3-2.8 5.3l-29.5 168.2a33.5 33.5 0 0 0 9.4 29.8c6.6 6.4 14.9 9.9 23.8 9.9zm67.4-174.4L687.8 215l73.3 73.3-362.7 362.6-88.9 15.7 15.6-89zM880 836H144c-17.7 0-32 14.3-32 32v36c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-36c0-17.7-14.3-32-32-32z"></path>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="action-button delete-button"
                              onClick={() => handleDeleteSubcategory(sub.nombre)}
                              title="Eliminar subcategoría"
                              disabled={loading}
                            >
                              <svg className="action-icon" viewBox="0 0 1024 1024">
                                <path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.1 0 62.3-26.9 63.9-61l24.7-523H896c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z"></path>
                              </svg>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Modal para Subcategorías
interface SubcategoryModalProps {
  subcategory: FinancialSubcategory | null;
  form: {
    nombre: string;
    grupo: string;
    codigos: { [key: string]: string };
    activo: boolean;
  };
  groups: FinancialGroup[];
  onFormChange: (form: any) => void;
  onSave: () => void;
  onClose: () => void;
  onAddCodigo: () => void;
  onRemoveCodigo: (codigo: string) => void;
  loading: boolean;
}

const SubcategoryModal: React.FC<SubcategoryModalProps> = ({
  subcategory,
  form,
  groups,
  onFormChange,
  onSave,
  onClose,
  onAddCodigo,
  onRemoveCodigo,
  loading
}) => (
  <div className="modal-overlay" onClick={() => !loading && onClose()}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h2 className="modal-title">
          {subcategory ? 'Editar Subcategoría' : 'Agregar Subcategoría'}
        </h2>
        <button className="modal-close" onClick={onClose} disabled={loading}>
          <svg viewBox="0 0 1024 1024">
            <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 0 0 203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"></path>
          </svg>
        </button>
      </div>
      <div className="modal-body">
        <form className="form" onSubmit={(e) => { e.preventDefault(); onSave(); }}>
          <div className="form-group">
            <label className="form-label">Nombre de la Subcategoría</label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => onFormChange({ ...form, nombre: e.target.value })}
              className="form-input"
              placeholder="Ej: Ventas, Combustible, Administrativo"
              required
            />
          </div>

  <div className="form-group">
            <label className="form-label">Grupo</label>
            <select
              value={form.grupo}
              onChange={(e) => onFormChange({ ...form, grupo: e.target.value, subcategoria: '' })}
              className="form-select"
              required
            >
              <option value="">Seleccionar grupo</option>
              {groups.map(group => (
                <option key={group.nombre} value={group.nombre}>
                  {group.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Códigos</label>
            <div className="codigos-section">
              <div className="codigos-list">
                {Object.entries(form.codigos).map(([codigo, tipo]) => (
                  <div key={codigo} className="codigo-item">
                    <span className="codigo-badge" style={{ backgroundColor: getClasificacionColor(tipo) }}>
                      {codigo}: {tipo}
                    </span>
                    <button
                      type="button"
                      className="remove-codigo-button"
                      onClick={() => onRemoveCodigo(codigo)}
                    >
                      <svg viewBox="0 0 1024 1024">
                        <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 0 0 203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"></path>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="add-codigo-button"
                onClick={(e) => {
                  console.log('🟢 CLICK EN BOTÓN AGREGAR CÓDIGO');
                  console.log('Evento:', e);
                  console.log('Función onAddCodigo:', onAddCodigo);
                  onAddCodigo();
                }}
              >
                <svg className="add-icon" viewBox="0 0 1024 1024">
                  <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm192 472c0 4.4-3.6 8-8 8H544v152c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V544H328c-4.4 0-8-3.6-8-8v-48c0-4.4 3.6-8 8-8h152V328c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v152h152c4.4 0 8 3.6 8 8v48z"></path>
                </svg>
                Agregar Código
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                checked={form.activo}
                onChange={(e) => onFormChange({ ...form, activo: e.target.checked })}
                className="form-checkbox"
              />
              Subcategoría activa
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose} disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="save-button" disabled={loading}>
              {loading ? 'Guardando...' : (subcategory ? 'Actualizar' : 'Guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
);

// Modal para Excepciones
interface ExceptionModalProps {
  exception: FincaException | null;
  form: {
    codigo: string;
    finca: string;
    subcategoria: string;
    grupo: string;
    clasificacion: 'costo' | 'gasto' | 'ingreso';
  };
  groups: FinancialGroup[];
  subcategories: FinancialSubcategory[];
  fincas: Array<{ nombre: string }>;
  onFormChange: (form: any) => void;
  onSave: () => void;
  onClose: () => void;
  loading: boolean;
}

const ExceptionModal: React.FC<ExceptionModalProps> = ({
  exception,
  form,
  groups,
  subcategories,
  fincas,
  onFormChange,
  onSave,
  onClose,
  loading
}) => (
  <div className="modal-overlay" onClick={() => !loading && onClose()}>
    <div className="modal-content exception-modal-extended" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h2 className="modal-title">
          {exception ? 'Editar Excepción por Finca' : 'Agregar Excepción por Finca'}
        </h2>
        <button className="modal-close" onClick={onClose} disabled={loading}>
          <svg viewBox="0 0 1024 1024">
            <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 0 0 203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"></path>
          </svg>
        </button>
      </div>
      <div className="modal-body">
        <form className="form" onSubmit={(e) => { e.preventDefault(); onSave(); }}>
          <div className="form-group">
            <label className="form-label">Código</label>
            <input
              type="text"
              value={form.codigo}
              onChange={(e) => onFormChange({ ...form, codigo: e.target.value })}
              className="form-input"
              placeholder="Ej: 7105059503"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Finca</label>
            <select
              value={form.finca}
              onChange={(e) => onFormChange({ ...form, finca: e.target.value })}
              className="form-select"
              required
            >
              <option value="">Seleccionar finca</option>
              {fincas.map(finca => (
                <option key={finca.nombre} value={finca.nombre}>
                  {finca.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Grupo</label>
            <select
              value={form.grupo}
              onChange={(e) => onFormChange({ ...form, grupo: e.target.value })}
              className="form-select"
              required
            >
              <option value="">Seleccionar grupo</option>
              {groups.map(group => (
                <option key={group.nombre} value={group.nombre}>
                  {group.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Subcategoría</label>
            <select
              value={form.subcategoria}
              onChange={(e) => onFormChange({ ...form, subcategoria: e.target.value })}
              className="form-select"
              required
            >
              <option value="">Seleccionar subcategoría</option>
              {subcategories
                .filter(sub => !form.grupo || sub.grupo === form.grupo)
                .map(sub => (
                  <option key={`${sub.grupo}-${sub.nombre}`} value={sub.nombre}>
                    {sub.nombre}
                  </option>
                ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Clasificación</label>
            <select
              value={form.clasificacion}
              onChange={(e) => onFormChange({ ...form, clasificacion: e.target.value as 'costo' | 'gasto' | 'ingreso' })}
              className="form-select"
              required
            >
              <option value="costo">Costo</option>
              <option value="gasto">Gasto</option>
              <option value="ingreso">Ingreso</option>
            </select>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose} disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="save-button" disabled={loading}>
              {loading ? 'Guardando...' : (exception ? 'Actualizar' : 'Guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
);

// (duplicado eliminado)

// Modal para mostrar códigos
interface CodigosModalProps {
  entity: {
    type: 'group' | 'subcategory' | 'finca';
    name: string;
    codigos: { [key: string]: string };
    parentName?: string; // Grupo para subcategorías
    id_padre?: string;   // id_padre del grupo (para DELETE code)
  };
  descripciones: { [key: string]: string };
  onClose: () => void;
  onAddCodigo?: () => void;
}

const CodigosModal: React.FC<CodigosModalProps> = ({ entity, descripciones, onClose, onAddCodigo }) => {
  const [searchCode, setSearchCode] = useState<string>('');
  const [showNotFoundMessage, setShowNotFoundMessage] = useState<boolean>(false);

  const handleSearchCode = (): void => {
    if (!searchCode.trim()) return;

    const codigoToSearch = searchCode.trim();
    const codigoExists = entity.codigos.hasOwnProperty(codigoToSearch);

    if (codigoExists) {
      // Buscar el elemento en el DOM y hacer scroll
      const codigoElement = document.querySelector(`[data-codigo="${codigoToSearch}"]`);
      if (codigoElement) {
        codigoElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        // Resaltar temporalmente el elemento
        codigoElement.classList.add('highlight-search');
        setTimeout(() => {
          codigoElement.classList.remove('highlight-search');
        }, 2000);
      }
      setShowNotFoundMessage(false);
    } else {
      // Mostrar mensaje de no encontrado
      setShowNotFoundMessage(true);
      setTimeout(() => {
        setShowNotFoundMessage(false);
      }, 3000);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      handleSearchCode();
    }
  };

  const handleDeleteCodigo = async (codigo: string): Promise<void> => {
    // Buscar la descripción del código para mostrarla en el mensaje
    const descripcion = descripciones[codigo] || codigo;
    
    // Verificar que tengamos los datos necesarios para eliminar (API requiere subcategoria e id_padre)
    if (entity.type !== 'subcategory' || !entity.name) {
      alert('No se puede eliminar el código: información de subcategoría incompleta.');
      return;
    }
    
    const confirmMessage = `¿Está seguro de que desea eliminar el código "${codigo}" (${descripcion}) de la subcategoría "${entity.name}"?\n\nEsta acción no se puede deshacer.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        console.log('🗑️ Eliminando código:', codigo, 'de subcategoría:', entity.name);
        const deleteBody: { subcategoria: string; id_padre?: string } = { subcategoria: entity.name };
        if (entity.id_padre) deleteBody.id_padre = entity.id_padre;
        
        await apiService.delete(`/api/financial-subcategories/codes/${codigo}`, {
          data: deleteBody
        });
        
        console.log('✅ Código eliminado exitosamente de la subcategoría');
        alert(`El código "${codigo}" ha sido eliminado exitosamente de la subcategoría "${entity.name}".`);
        
        // Cerrar el modal para que el componente padre recargue los datos
        onClose();
        
      } catch (error: any) {
        console.error('❌ Error al eliminar código:', error);
        
        if (error.response?.status === 404) {
          alert(`El código "${codigo}" no existe en esta subcategoría o la subcategoría no existe.`);
        } else {
          alert(`Error al eliminar el código "${codigo}": ${error.response?.data?.error || error.response?.data?.message || error.message}`);
        }
      }
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content codigos-modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <div className="modal-header-top">
          <h2 className="modal-title">
            Códigos de {entity.type === 'group' ? 'Grupo' : entity.type === 'subcategory' ? 'Subcategoría' : 'Finca'}: {entity.name}
          </h2>
          <button 
            className="add-codigo-button"
            onClick={() => {
              console.log('🔵 Click en botón AGREGAR CÓDIGO del modal de códigos');
              if (onAddCodigo) {
                onAddCodigo();
              } else {
                console.warn('⚠️ onAddCodigo no está definido');
              }
            }}
            title="Agregar nuevo código"
          >
            <svg className="add-icon" viewBox="0 0 1024 1024">
              <path d="M620 384h-128v-128h-64v128h-128v64h128v128h64v-128h128zM512 1024c-282.77 0-512-229.23-512-512s229.23-512 512-512 512 229.23 512 512-229.23 512-512 512zM512 960c247.42 0 448-200.58 448-448s-200.58-448-448-448-448 200.58-448 448 200.58 448 448 448z"></path>
            </svg>
            AGREGAR CÓDIGO
          </button>
        </div>
        
        <div className="modal-header-bottom">
          <div className="search-container">
            <button 
              className="search-button"
              onClick={handleSearchCode}
              title="Buscar código"
            >
              <svg viewBox="0 0 1024 1024" width="20" height="20">
                <path d="M909.6 854.5L649.9 594.8C690.2 542.7 712 479 712 412c0-80.2-31.3-155.4-87.9-212.1-56.6-56.7-132-87.9-212.1-87.9s-155.5 31.3-212.1 87.9C143.2 256.5 112 331.8 112 412c0 80.1 31.3 155.5 87.9 212.1C256.5 680.8 331.8 712 412 712c67 0 130.6-21.8 182.7-62l259.7 259.6a8.2 8.2 0 0 0 11.6 0l43.6-43.5a8.2 8.2 0 0 0 0-11.6zM570.4 570.4C528 612.7 471.8 636 412 636s-116-23.3-158.4-65.6C211.3 528 188 471.8 188 412s23.3-116 65.6-158.4C296 211.3 352.2 188 412 188s116 23.3 158.4 65.6C612.7 296 636 352.2 636 412s-23.3 116-65.6 158.4z"></path>
              </svg>
            </button>
            <input
              type="text"
              className="search-input"
              placeholder="Buscar código"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </div>
          <button className="modal-close" onClick={onClose}>
            <svg viewBox="0 0 1024 1024">
              <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 0 0 203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"></path>
            </svg>
          </button>
        </div>
        
        {showNotFoundMessage && (
          <div className="not-found-message">
            <svg viewBox="0 0 1024 1024" width="16" height="16">
              <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"></path>
              <path d="M464 336a48 48 0 1 0 96 0 48 48 0 1 0-96 0zm72 112h-48c-4.4 0-8 3.6-8 8v272c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V456c0-4.4-3.6-8-8-8z"></path>
            </svg>
            El código "{searchCode}" no está asignado a esta subcategoría
          </div>
        )}
      </div>
      <div className="modal-body">
        <div className="codigos-table-container">
          <div className="codigos-table-header">
            <div className="codigos-table-header-cell">Código</div>
            <div className="codigos-table-header-cell">Descripción</div>
            <div className="codigos-table-header-cell">Eliminar</div>
          </div>
          
          <div className="codigos-table-body">
            {Object.keys(entity.codigos).length === 0 ? (
              <div className="no-codigos-row">
                <div className="no-codigos-cell">
                  <svg className="no-codigos-icon" viewBox="0 0 1024 1024">
                    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"></path>
                  </svg>
                  <p>No hay códigos asociados</p>
                </div>
              </div>
            ) : (
              Object.entries(entity.codigos).map(([codigo, tipo]) => {
                // Asegurar que tipo sea siempre un string
                const tipoString = typeof tipo === 'string' ? tipo : String(tipo);
                
                return (
                  <div key={codigo} className="codigos-table-row" data-codigo={codigo}>
                    <div className="codigos-table-cell codigo-cell" data-label="Código">
                      <span className="codigo-number">{codigo}</span>
                      <span 
                        className="codigo-type-badge"
                        style={{ backgroundColor: getClasificacionColor(tipoString) }}
                      >
                        {tipoString}
                      </span>
                    </div>
                    <div className="codigos-table-cell descripcion-cell" data-label="Descripción">
                      <span className="codigo-description">
                        {descripciones[codigo] || (tipoString === 'ingreso' ? 'Genera ingresos' : 
                         tipoString === 'costo' ? 'Representa un costo' : 
                         'Es un gasto operativo')}
                      </span>
                    </div>
                    <div className="codigos-table-cell action-cell" data-label="Eliminar">
                      <button
                        className="delete-codigo-button"
                        onClick={() => handleDeleteCodigo(codigo)}
                        title="Eliminar código"
                      >
                        <svg viewBox="0 0 1024 1024" width="16" height="16">
                          <path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.1 0 62.3-26.9 63.9-61l24.7-523H896c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

// Modal avanzado para agregar código
interface AddCodigoModalAdvancedProps {
  targetSubcategory: { nombre: string; grupo: string; id_padre?: string };
  form: {
    codigo: string;
    descripcion: string;
    tipo: 'costo' | 'gasto' | 'ingreso';
  };
  codigoExistente: {
    existe: boolean;
    descripcion?: string;
    subcategoria?: string;
    grupo?: string;
    tipo?: string;
  };
  onFormChange: (form: any) => void;
  onSearchCodigo: (codigo: string) => void;
  onSave: () => void;
  onClose: () => void;
  loading: boolean;
  searchingCodigo: boolean;
}

const AddCodigoModalAdvanced: React.FC<AddCodigoModalAdvancedProps> = ({
  targetSubcategory,
  form,
  codigoExistente,
  onFormChange,
  onSearchCodigo,
  onSave,
  onClose,
  loading,
  searchingCodigo
}) => {
  const [codigoInput, setCodigoInput] = React.useState<string>('');
  const [hasSearched, setHasSearched] = React.useState<boolean>(false);

  const handleCodigoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCodigo = e.target.value;
    setCodigoInput(newCodigo);
    onFormChange({ ...form, codigo: newCodigo });
    setHasSearched(false);
  };

  const handleBuscarCodigo = () => {
    if (codigoInput.trim()) {
      onSearchCodigo(codigoInput);
      setHasSearched(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBuscarCodigo();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-codigo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            Agregar Código a: {targetSubcategory.nombre}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg viewBox="0 0 1024 1024">
              <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 0 0 203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z"></path>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="add-codigo-form">
            {/* Campo de código con botón de buscar */}
            <div className="form-group">
              <label className="form-label">Código</label>
              <div className="codigo-input-group">
                <input
                  type="text"
                  value={codigoInput}
                  onChange={handleCodigoChange}
                  onKeyPress={handleKeyPress}
                  className="form-input"
                  placeholder="Ej: 61010105"
                  disabled={searchingCodigo || loading}
                />
                <button
                  type="button"
                  className="search-codigo-button"
                  onClick={handleBuscarCodigo}
                  disabled={searchingCodigo || loading || !codigoInput.trim()}
                  title="Buscar código"
                >
                  <svg viewBox="0 0 1024 1024" width="20" height="20">
                    <path d="M909.6 854.5L649.9 594.8C690.2 542.7 712 479 712 412c0-80.2-31.3-155.4-87.9-212.1-56.6-56.7-132-87.9-212.1-87.9s-155.5 31.3-212.1 87.9C143.2 256.5 112 331.8 112 412c0 80.1 31.3 155.5 87.9 212.1C256.5 680.8 331.8 712 412 712c67 0 130.6-21.8 182.7-62l259.7 259.6a8.2 8.2 0 0 0 11.6 0l43.6-43.5a8.2 8.2 0 0 0 0-11.6zM570.4 570.4C528 612.7 471.8 636 412 636s-116-23.3-158.4-65.6C211.3 528 188 471.8 188 412s23.3-116 65.6-158.4C296 211.3 352.2 188 412 188s116 23.3 158.4 65.6C612.7 296 636 352.2 636 412s-23.3 116-65.6 158.4z"></path>
                  </svg>
                  {searchingCodigo ? 'Buscando...' : 'Buscar'}
                </button>
              </div>
            </div>

            {/* Mostrar loading mientras busca */}
            {searchingCodigo && (
              <div className="searching-codigo-container">
                <Loader />
                <p className="searching-text">Buscando código en la base de datos...</p>
              </div>
            )}

            {/* Mostrar información si el código existe */}
            {!searchingCodigo && hasSearched && codigoExistente.existe && (
              <div className="codigo-existente-info">
                <div className="info-header">
                  <svg viewBox="0 0 1024 1024" width="24" height="24" fill="#f39c12">
                    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"></path>
                    <path d="M464 336a48 48 0 1 0 96 0 48 48 0 1 0-96 0zm72 112h-48c-4.4 0-8 3.6-8 8v272c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V456c0-4.4-3.6-8-8-8z"></path>
                  </svg>
                  <h3>⚠️ Este código ya existe</h3>
                </div>
                <div className="info-content">
                  <p><strong>Descripción:</strong> {codigoExistente.descripcion || 'Sin descripción'}</p>
                  <p><strong>Subcategoría actual:</strong> {codigoExistente.subcategoria}</p>
                  <p><strong>Grupo actual:</strong> {codigoExistente.grupo}</p>
                  <p>
                    <strong>Tipo:</strong>{' '}
                    <span 
                      className="tipo-badge"
                      style={{ backgroundColor: getClasificacionColor(codigoExistente.tipo || '') }}
                    >
                      {codigoExistente.tipo}
                    </span>
                  </p>
                  <div className="warning-message">
                    <svg viewBox="0 0 1024 1024" width="20" height="20">
                      <path d="M955.7 856l-416-720c-6.2-10.7-16.9-16-27.7-16s-21.6 5.3-27.7 16l-416 720C56 877.4 71.4 904 96 904h832c24.6 0 40-26.6 27.7-48zM480 416c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v184c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V416zm32 352a48.01 48.01 0 0 1 0-96 48.01 48.01 0 0 1 0 96z"></path>
                    </svg>
                    Al guardar, este código se eliminará de <strong>"{codigoExistente.subcategoria}"</strong> y se agregará a <strong>"{targetSubcategory.nombre}"</strong>
                  </div>
                </div>
              </div>
            )}

            {/* Mostrar formulario si el código NO existe */}
            {!searchingCodigo && hasSearched && !codigoExistente.existe && (
              <div className="codigo-nuevo-info">
                <div className="info-header success">
                  <svg viewBox="0 0 1024 1024" width="24" height="24" fill="#27ae60">
                    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm193.5 301.7l-210.6 292a31.8 31.8 0 0 1-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.2 0 19.9 4.9 25.9 13.3l71.2 98.8 157.2-218c6-8.3 15.6-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.5 12.7z"></path>
                  </svg>
                  <h3>✅ Código nuevo - Complete la información</h3>
                </div>

                <div className="form-group">
                  <label className="form-label">Descripción del código</label>
                  <input
                    type="text"
                    value={form.descripcion}
                    onChange={(e) => onFormChange({ ...form, descripcion: e.target.value })}
                    className="form-input"
                    placeholder="Ej: Fertilizante NPK 10-30-10"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tipo de código</label>
                  <select
                    value={form.tipo}
                    onChange={(e) => onFormChange({ ...form, tipo: e.target.value as 'costo' | 'gasto' | 'ingreso' })}
                    className="form-select"
                    disabled={loading}
                  >
                    <option value="costo">Costo</option>
                    <option value="gasto">Gasto</option>
                    <option value="ingreso">Ingreso</option>
                  </select>
                </div>
              </div>
            )}

            {/* Botones de acción con estilo boton4 */}
            <div className="form-actions-boton4">
              <button 
                type="button" 
                className="btn-boton4 btn-cancel" 
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="btn-boton4 btn-save" 
                onClick={onSave}
                disabled={loading || !hasSearched || !codigoInput.trim()}
              >
                {loading ? 'Guardando...' : codigoExistente.existe ? 'Mover Código' : 'Agregar Código'}
              </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default AdministradorCodigos;
